import { css } from "@emotion/css";
import type { PrimaryPageAction, SimpleMenuItem } from "@octopusdeploy/design-system-components";
import { Button, ActionButtonType, Callout, Chip, InlineMessage, noOp, EmptyStateOnboarding, EmptyStatePlatformHubPoliciesIllustration, Pagination } from "@octopusdeploy/design-system-components";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { GetPolicySummariesBffResponse, PlatformHubVersionControlSettingsResource, PolicySummary, Repository, ValidateGitRefV2Response } from "@octopusdeploy/octopus-server-client";
import { Permission, ValidateGitRefV2ResponseType } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import type { ReactElement } from "react";
import * as React from "react";
import { lastAccessedPlatformHubGitRef } from "~/areas/platformHub/LastAccessedPlatformHubGitRef";
import { PlatformHubPageHeaderBranchSelector } from "~/areas/platformHub/PlatformHubPageHeaderBranchSelector";
import { PlatformHubVcsError } from "~/areas/platformHub/PlatformHubVcsError";
import type { CreatePolicyDialogProps } from "~/areas/platformHub/policies/CreatePolicyDialog";
import { CreatePolicyDialog } from "~/areas/platformHub/policies/CreatePolicyDialog";
import { usePlatformHubGitRef } from "~/areas/platformHub/usePlatformHubGitRef";
import { client, repository } from "~/clientInstance";
import OpenDialogButton from "~/components/Dialog/OpenDialogButton";
import FilterSearchBox from "~/components/FilterSearchBox/index";
import ExternalLink from "~/components/Navigation/ExternalLink/index";
import InternalLink from "~/components/Navigation/InternalLink/index";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import type { MenuItem } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { PageContent } from "~/components/PageContent/PageContent";
import { hasPermission, isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { getValueInStorage } from "~/hooks/localStorageHelpers";
import useLocalStorage from "~/hooks/useLocalStorage";
import { DataTable, DataTableBody, DataTableHeader, DataTableHeaderColumn, DataTableRow, DataTableRowColumn } from "~/primitiveComponents/dataDisplay/DataTable/index";
import DateFormatter from "~/utils/DateFormatter/index";
interface PoliciesPageProps {
    loaderData: LoaderData;
    queryParams: PoliciesPageQueryParams;
    setQueryParams: QueryParamValuesSetter<PoliciesPageQueryParams>;
}
interface LoaderData {
    policiesResponse: GetPolicySummariesBffResponse;
    persistenceSettings: PlatformHubVersionControlSettingsResource;
    gitRefValidationResult: ValidateGitRefV2Response | undefined;
}
export interface PoliciesPageQueryParams {
    searchValue: string;
    page: number | undefined;
    pageSize: number | undefined;
    gitRef: string;
}
const defaultPageSize = 30;
const emptyPoliciesResponse: GetPolicySummariesBffResponse = {
    Policies: [],
    FilteredItemsCount: 0,
    ItemsPerPage: 0,
    TotalItemsCount: 0,
};
export async function policiesPageLoader(repository: Repository, queryParams: PoliciesPageQueryParams): Promise<LoaderData> {
    const persistenceSettings = await repository.PlatformHub.getVersionControlSettings();
    if (!persistenceSettings.Url) {
        return {
            policiesResponse: emptyPoliciesResponse,
            persistenceSettings,
            gitRefValidationResult: undefined,
        };
    }
    let gitRef = queryParams.gitRef;
    if (!gitRef) {
        gitRef = lastAccessedPlatformHubGitRef.get(persistenceSettings);
    }
    const gitRefValidationResult = await repository.PlatformHub.validateGitRef(gitRef);
    if (gitRefValidationResult.Type !== ValidateGitRefV2ResponseType.Success) {
        return {
            policiesResponse: emptyPoliciesResponse,
            persistenceSettings,
            gitRefValidationResult,
        };
    }
    const userPageSize = getValueInStorage(`Octopus.PoliciesList.PageSize`);
    const pageSize = userPageSize ? parseInt(userPageSize) : defaultPageSize;
    const policiesResponse = await repository.Policy.getSummaries({
        skip: ((queryParams.page ?? 1) - 1) * (queryParams.pageSize ?? pageSize),
        take: queryParams.pageSize ?? pageSize,
        partialName: queryParams.searchValue,
        gitRef: gitRefValidationResult.GitRef?.CanonicalName ?? gitRef,
    });
    return {
        policiesResponse,
        persistenceSettings: persistenceSettings,
        gitRefValidationResult,
    };
}
export function PoliciesPage({ loaderData, queryParams, setQueryParams }: PoliciesPageProps) {
    const [gitRef, setGitRef, validationResult] = usePlatformHubGitRef(loaderData.persistenceSettings, queryParams, setQueryParams, loaderData.gitRefValidationResult);
    const [userDefaultPageSize, setUserDefaultPageSize] = useLocalStorage(`Octopus.PoliciesList.PageSize`, defaultPageSize);
    const { result: pagingData, refetch: reload } = useQuery((repository) => {
        if (!gitRef) {
            return Promise.resolve(emptyPoliciesResponse);
        }
        if (validationResult && validationResult.Type !== ValidateGitRefV2ResponseType.Success) {
            return Promise.resolve(emptyPoliciesResponse);
        }
        return repository.Policy.getSummaries({
            skip: ((queryParams.page ?? 1) - 1) * (queryParams.pageSize ?? userDefaultPageSize),
            take: queryParams.pageSize ?? userDefaultPageSize,
            partialName: queryParams.searchValue,
            gitRef,
        });
    }, [gitRef, queryParams.page, queryParams.pageSize, queryParams.searchValue, userDefaultPageSize, validationResult], "Get paginated policies", {
        initialResult: loaderData.policiesResponse,
    });
    const onPageChange = (page: number) => {
        setQueryParams({ ...queryParams, page });
    };
    const onPageSizeChange = (pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
        setUserDefaultPageSize(pageSize);
    };
    const onSearch = (value: string) => {
        setQueryParams({ ...queryParams, searchValue: value, page: 1 });
    };
    if (!gitRef || !loaderData.persistenceSettings.Url) {
        return (<PageContent header={getPoliciesHeader()}>
                <EmptyPoliciesOnboarding actionString={"To add a policy, you\u2019ll need to set up a Git connection."} primaryAction={{
                type: "navigate",
                label: "Configure Version Control",
                hasPermissions: isAllowed({ permission: Permission.PlatformHubEdit }),
                onClick: noOp,
                path: links.platformHubVersionControlSettingsPage.generateUrl(),
            }}/>
            </PageContent>);
    }
    const showOnboarding = pagingData.TotalItemsCount === 0;
    const titleAccessory = <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} persistenceSettings={loaderData.persistenceSettings}/>;
    const overflowActions: SimpleMenuItem[] = [];
    if (isAllowed({ permission: Permission.EventView, wildcard: true })) {
        overflowActions.push({
            type: "button",
            label: "Download audit log",
            onClick: () => {
                window.location.href = client.resolveLinkTemplate("Events", { eventGroups: ["CompliancePolicyEvaluated"], asCsv: true });
            },
        });
    }
    const addPolicyButton: PrimaryPageAction = {
        type: "custom",
        key: "Create Policy",
        hasPermissions: isAllowed({ permission: Permission.PlatformHubEdit }),
        content: <CreatePolicyButton gitRef={gitRef}/>,
    };
    if (validationResult !== undefined && validationResult.Type !== ValidateGitRefV2ResponseType.Success) {
        return (<PageContent header={getPoliciesHeader(titleAccessory)}>
                <PlatformHubVcsError error={validationResult} gitRef={gitRef} persistenceSettings={loaderData.persistenceSettings} setGitRef={setGitRef}/>
            </PageContent>);
    }
    if (showOnboarding) {
        return (<PageContent header={getPoliciesHeader(titleAccessory)}>
                <EmptyPoliciesOnboarding primaryAction={addPolicyButton}/>
            </PageContent>);
    }
    return (<PageContent header={getPoliciesHeader(titleAccessory, addPolicyButton, overflowActions)} filters={{
            inputs: [<FilterSearchBox value={queryParams.searchValue} onChange={onSearch} placeholder={"Filter by name..."} autoFocus={false} debounceDelay={500}/>],
        }} pagination={{
            ui: (<Pagination label="Policies" totalResults={pagingData.FilteredItemsCount} itemsPerPageOptions={[30, 50, 100]} selectedItemsPerPage={pagingData.ItemsPerPage} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: "bottom",
        }}>
            <DataTable>
                <DataTableHeader>
                    <DataTableRow>
                        <DataTableHeaderColumn>Name</DataTableHeaderColumn>
                        <DataTableHeaderColumn>Active version</DataTableHeaderColumn>
                        <DataTableHeaderColumn>Last published</DataTableHeaderColumn>
                    </DataTableRow>
                </DataTableHeader>
                <DataTableBody>
                    {pagingData.Policies.map((p) => (<PolicyRow policy={p} key={p.Id} reload={reload}/>))}
                </DataTableBody>
            </DataTable>
        </PageContent>);
}
const EmptyPoliciesOnboarding = ({ actionString, primaryAction }: {
    actionString?: string;
    primaryAction?: PrimaryPageAction;
}) => (<EmptyStateOnboarding title="Enforce deployment policies" description={<>
                <p>Ensure that your teams are compliant and secure by default. You can craft and manage deployment rules that can be applied across projects and spaces.</p>
                {actionString ? <p>{actionString}</p> : null}
            </>} image={<EmptyStatePlatformHubPoliciesIllustration />} primaryAction={primaryAction} learnMore={<ExternalLink href="policies">Docs: Policies</ExternalLink>}/>);
const getPoliciesHeader = (contextSelector?: ReactElement, primaryAction?: PrimaryPageAction, overflowActions?: SimpleMenuItem[]) => {
    return { title: "Policies", contextSelector, overflowActions, primaryAction };
};
interface PolicyRowProps {
    policy: PolicySummary;
    reload: () => void;
}
function PolicyRow({ policy, reload }: PolicyRowProps) {
    const linkTarget = links.editPolicyPage.generateUrl({ slug: policy.Slug }, { gitRef: policy.GitRef });
    const navigate = useSpaceAwareNavigation();
    const publishButton = hasPermission(Permission.PlatformHubEdit) ? (<Button label={"Publish"} importance={"quiet"} accessibleName={"Publish"} onClick={() => {
            navigate.navigate(links.editPolicyPage.generateUrl({ slug: policy.Slug }, { gitRef: policy.GitRef, autoPublish: true }));
        }}/>) : null;
    if (policy.OclError) {
        return (<DataTableRow className={styles.middle}>
                <DataTableRowColumn colSpan={4} className={styles.middle}>
                    <div className={styles.nameContainer}>
                        <div>{policy.Name}.ocl</div>
                        <InlineMessage type={"danger"} description={policy.OclError}/>
                    </div>
                </DataTableRowColumn>
            </DataTableRow>);
    }
    return (<DataTableRow className={styles.middle}>
            <DataTableRowColumn className={styles.middle}>
                <div className={styles.nameContainer}>
                    <InternalLink to={linkTarget}>
                        <div>{policy.Name}</div>
                    </InternalLink>
                    <PolicyWarning policy={policy}/>
                </div>
            </DataTableRowColumn>
            <DataTableRowColumn className={styles.middle}>
                <PolicyActiveVersion policy={policy}/>
            </DataTableRowColumn>
            <DataTableRowColumn className={styles.middle}>
                {policy.LastPublishedDate ? (<div>
                        {policy.LastPublishedVersion} ({DateFormatter.dateToLongFormat(policy.LastPublishedDate)})
                    </div>) : (publishButton)}
            </DataTableRowColumn>
            <DataTableRowColumn className={styles.right}>
                <PolicyOverflowMenu policy={policy} reloadPolicies={reload}></PolicyOverflowMenu>
            </DataTableRowColumn>
        </DataTableRow>);
}
interface PolicyWarningProps {
    policy: PolicySummary;
}
function PolicyWarning({ policy }: PolicyWarningProps) {
    if (policy.Warning) {
        return <InlineMessage type={"danger"} description={policy.Warning.Message}/>;
    }
    return null;
}
interface PolicyActiveVersionProps {
    policy: PolicySummary;
}
function PolicyActiveVersion({ policy }: PolicyActiveVersionProps) {
    if (policy.ActiveVersion) {
        return <Chip backgroundColor={themeTokens.color.badge.background.info} label={policy.ActiveVersion} labelColor={themeTokens.color.badge.text.primary} noMargin={true}/>;
    }
    if (policy.LastPublishedDate) {
        return <Chip backgroundColor={themeTokens.color.badge.background.neutral} label={"Inactive"} labelColor={themeTokens.color.badge.text.secondary} noMargin={true}/>;
    }
    return <Chip backgroundColor={themeTokens.color.badge.background.neutral} label={"Unpublished"} labelColor={themeTokens.color.badge.text.secondary} noMargin={true}/>;
}
interface PolicyOverflowMenuProps {
    policy: PolicySummary;
    reloadPolicies: () => void;
}
function PolicyOverflowMenu({ policy, reloadPolicies }: PolicyOverflowMenuProps) {
    const overFlowActions: Array<MenuItem | MenuItem[]> = [];
    const version = policy.ActiveVersion;
    if (version) {
        const deactivate = OverflowMenuItems.destructiveItem("Deactivate", "Are you sure you want to deactivate this policy?", "Deactivate", "Deactivating...", async () => {
            await repository.Policy.updateActivationStatus({ Slug: policy.Slug, Version: version, IsActive: false });
            reloadPolicies();
        }, <Callout type={"warning"} title={"Stop evaluating policy"}>
                <div>
                    By <strong>deactivating</strong> the policy <strong>{policy.Name}</strong> will no longer be evaluated.
                </div>
            </Callout>, { permission: Permission.PlatformHubEdit });
        overFlowActions.push(deactivate);
    }
    return <OverflowMenu menuItems={overFlowActions}/>;
}
function CreatePolicyButton({ gitRef }: CreatePolicyDialogProps) {
    return (<OpenDialogButton disabled={false} label={"Create Policy"} title={"Create new policy"} type={ActionButtonType.Primary}>
            <CreatePolicyDialog gitRef={gitRef}/>
        </OpenDialogButton>);
}
const styles = {
    middle: css({
        verticalAlign: "middle !important",
    }),
    right: css({
        verticalAlign: "middle !important",
        float: "right",
    }),
    nameContainer: css({ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: space[4] }),
    description: css({
        paddingLeft: space[1],
        display: "flex",
        alignItems: "center",
        gap: space[4],
    }),
};
